import { IconCircleCheckFilled, IconDots, IconPencil, IconThumbUp, IconTrash } from '@tabler/icons-react'
import groupBy from 'lodash/groupBy'
import { ReactNode, type RefObject, useState } from 'react'
import { Entries } from 'type-fest'

import { AstraAvatar, AstraButton, AstraDropdownItem, AstraDropdownMenu, AstraIcon, AstraParagraph, astraTwMerge, day, humanizeDate } from '~/astra'
import { useStore } from '~/store/store'
import { CommentsMessage, CommentsMessageReaction, CommentsThread, CommentsThreadReaction } from '~/storm'
import { getTimeStampText } from '~/utils/getTimeStampText'
import { getInitials } from '~/utils/user'

import { getReactionEmoji, isValidReaction, ReactionString } from '../RichTextEditing/reactions'
import type { ThreadStatus } from './CommentThread'
import { LexicalEditor, LexicalEditorProps } from './lexical/LexicalEditor'
import { ThreadSourceIcon } from './ThreadSourceIcon'
import { isLikelyOwner } from './utils'

type Reaction = CommentsMessageReaction | CommentsThreadReaction

type ValidReaction = Omit<Reaction, 'reaction'> & { reaction: ReactionString }

const StatusIndicator = ({ status }: { status: ThreadStatus }) => (
    <span className="h-[16px] w-[16px] text-subtle">{status === 'resolved' && <AstraIcon icon={IconCircleCheckFilled} size="small" />}</span>
)

export const AddReactionIcon = () => <IconThumbUp />

const ReactionGroups = ({ reactions, onReaction }: { reactions: Reaction[]; onReaction: (reaction: ReactionString) => void }) => {
    const validReactions = reactions.filter(reaction => isValidReaction(reaction.reaction)) as ValidReaction[]
    const groups = groupBy(validReactions, 'reaction') as Record<ReactionString, ValidReaction[]>
    const user = useStore(state => state.user)

    const isAnyReactionCurrentUser = (reactions: ValidReaction[]) => reactions.some(reaction => isLikelyOwner(reaction, user))

    return (
        <div className="flex items-center gap-1">
            {(Object.entries(groups) as Entries<typeof groups>).map(([reaction, reactions]) => (
                <button
                    type="button"
                    key={reaction}
                    title={reactions.map(reaction => reaction.updated_by).join(', ')}
                    className={astraTwMerge(
                        'flex min-w-[50px] items-center gap-2 rounded-xl border border-base px-2 py-0.5 hover:bg-neutral-subtle-hover',
                        isAnyReactionCurrentUser(reactions) ? 'bg-neutral-subtle-rest text-muted' : ''
                    )}
                    onClick={() => onReaction(reaction)}
                >
                    <span>{getReactionEmoji(reaction)}</span>
                    {String(reactions.length)}
                </button>
            ))}
        </div>
    )
}

const MessageHoverMenu = ({ onEdit, onDelete }: { onEdit?: () => void; onDelete?: () => void }) => (
    <AstraDropdownMenu
        sideOffset={8}
        dropdownContent={
            <>
                {onEdit && (
                    <AstraDropdownItem
                        option={{
                            type: 'action',
                            title: 'Rediger',
                            icon: () => <AstraIcon icon={IconPencil} size="small" />,
                            action: onEdit,
                        }}
                    />
                )}
                {onDelete && (
                    <AstraDropdownItem
                        option={{
                            type: 'action',
                            title: 'Slett',
                            icon: () => <AstraIcon icon={IconTrash} size="small" />,
                            action: onDelete,
                        }}
                    />
                )}
            </>
        }
    >
        <AstraButton variant="ghost" icon={<IconDots className="text-default" />} aria-label="Mer" />
    </AstraDropdownMenu>
)

export type CommentMessageProps = {
    status?: ThreadStatus
    children?: ReactNode
    ref?: RefObject<HTMLDivElement | null>
    content: CommentsMessage['content'] | CommentsThread['content']
    reactions?: Reaction[]
    onReaction?: (reaction: ReactionString) => void
    isParent?: boolean
    isEditing?: boolean
    onEdit?: LexicalEditorProps['onSendMessage']
    onCancelEdit?: LexicalEditorProps['onCancelClick']
    onEditClick?: () => void
    onDeleteClick?: () => void
    showHoverMenu?: boolean
    hideMetaInformation?: boolean
    sourcePath?: string | null
}

export const CommentMessage = ({
    children,
    ref,
    onReaction,
    status,
    content,
    reactions,
    isParent,
    showHoverMenu,
    hideMetaInformation = false,
    sourcePath,
    isEditing = false,
    onEdit,
    onCancelEdit,
    onEditClick,
    onDeleteClick,
}: CommentMessageProps) => {
    const initials = getInitials(content.updated_by || '')
    const [isHovering, setIsHovering] = useState(false)
    const shouldShowReactions = reactions && reactions.length > 0 && onReaction

    const hasBeenEdited = content.updated_at !== content.created_at
    const timeStamp = hasBeenEdited ? content.updated_at : content.created_at
    const time = day(timeStamp)

    return (
        <div ref={ref} className="relative flex flex-col gap-1.5" onMouseEnter={() => setIsHovering(true)} onMouseLeave={() => setIsHovering(false)}>
            {!hideMetaInformation && (
                <div className="flex min-h-[28px] justify-between">
                    <div className="flex items-center gap-1.5">
                        <AstraAvatar variant="initial" initials={initials} size="small" />
                        <AstraParagraph className="pl-1">{content.updated_by}</AstraParagraph>
                        <span className="flex items-center gap-1">
                            <AstraParagraph
                                weight="regular"
                                variant="body-small"
                                className="text-subtle"
                                title={`${hasBeenEdited ? 'Oppdatert' : 'Opprettet'} ${humanizeDate(time)}`}
                            >
                                {getTimeStampText(timeStamp)} {hasBeenEdited && '(endret)'}
                            </AstraParagraph>
                            {sourcePath && <ThreadSourceIcon sourcePath={sourcePath} />}
                        </span>
                    </div>

                    <div className="flex items-center gap-1">
                        {onReaction && isHovering && !isParent && (
                            <>
                                {!shouldShowReactions && (
                                    <AstraButton variant="ghost" icon={<AddReactionIcon />} aria-label="Lik" onClick={() => onReaction('like')} />
                                )}
                                {showHoverMenu && <MessageHoverMenu onEdit={onEditClick} onDelete={onDeleteClick} />}
                            </>
                        )}
                        {status && status !== 'active' && <StatusIndicator status={status} />}
                    </div>
                </div>
            )}

            <LexicalEditor
                editable={isEditing}
                initialValue={content.raw_content}
                onSendMessage={onEdit}
                autoFocus={isEditing}
                showCancelButton={isEditing}
                onCancelClick={onCancelEdit}
            />
            {shouldShowReactions && <ReactionGroups reactions={reactions} onReaction={onReaction} />}
            {children}
        </div>
    )
}
